import React, { useMemo } from "react";
import { Empty, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import "./flagPatterns.scss";

// Flags Intelligence — compact read-only view of the current red-flag
// concentrations. Each pattern already arrives with a plain-language `summary`
// ("5 access-constraint flags in Cluster X in the last 30 days"), so this panel
// leads with that sentence and adds the count/threshold, the implementing
// partner, and a drill-down into the flags behind it.
//
// Shared by the TPM Summary dashboard and the PM home screen, so the sorting
// rule and the pill vocabulary live in one place. `bare` drops the heading for
// hosts that already have a section header of their own (the dashboard).
const FlagPatternsPanel = ({
  patterns,
  loading = false,
  title = "Flag patterns",
  limit,
  bare = false,
  emptyText = "No flag concentrations right now — volumes are within the configured thresholds.",
}) => {
  const navigate = useNavigate();

  // Most-concentrated first: how far past its threshold a pattern is, then raw
  // count as the tie-break (a pattern with no threshold falls back to count).
  const sorted = useMemo(() => {
    const list = Array.isArray(patterns) ? [...patterns] : [];
    const pressure = (p) =>
      p?.threshold ? (p.count || 0) / p.threshold : p?.count || 0;
    list.sort((a, b) => pressure(b) - pressure(a) || (b?.count || 0) - (a?.count || 0));
    return limit ? list.slice(0, limit) : list;
  }, [patterns, limit]);

  return (
    <div className="flagPatterns">
      {!bare && (
        <div className="homePanelHead">
          <h3>{title}</h3>
          {sorted.length > 0 && (
            <span
              className="homePanelLink"
              onClick={() => navigate("/project/flags?Flags")}
            >
              Open Flags board
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flagPatternsLoading">
          <Spin />
        </div>
      ) : sorted.length === 0 ? (
        <Empty description={emptyText} />
      ) : (
        <div className="flagPatternsRows">
          {sorted.map((p, i) => {
            const breached = p.threshold != null && p.count >= p.threshold;
            const flagCount = p.flagIds?.length || 0;
            return (
              <div
                className="flagPatternsRow"
                key={`${p.issueType}-${p.clusterId || "noCluster"}-${
                  p.implementingPartnerId || "noIp"
                }-${i}`}
                onClick={() => navigate("/project/flags?Flags")}
                role="button"
              >
                <span
                  className={`flagPatternsCount ${
                    breached ? "flagPatternsCount--breached" : ""
                  }`}
                >
                  {p.count}
                  {p.threshold != null && (
                    <span className="flagPatternsCountSub">/{p.threshold}</span>
                  )}
                </span>

                <span className="flagPatternsBody">
                  <span className="flagPatternsSummary">
                    {p.summary ||
                      `${p.count} ${p.issueTypeLabel || ""} flags in the last ${
                        p.windowDays
                      } days`}
                  </span>
                  <span className="flagPatternsMeta">
                    {p.issueTypeLabel && (
                      <span className="flagPatternsPill flagPatternsPill--issue">
                        {p.issueTypeLabel}
                      </span>
                    )}
                    {/* Grouping dimension added by Flags Intelligence — null
                        when the flags aren't attributable to a single partner. */}
                    <span
                      className={`flagPatternsPill ${
                        p.implementingPartnerName
                          ? "flagPatternsPill--ip"
                          : "flagPatternsPill--neutral"
                      }`}
                    >
                      {p.implementingPartnerName || "No single IP"}
                    </span>
                    {p.region && (
                      <span className="flagPatternsPill flagPatternsPill--neutral">
                        {p.region}
                      </span>
                    )}
                    {breached && (
                      <span className="flagPatternsPill flagPatternsPill--breach">
                        Threshold exceeded
                      </span>
                    )}
                  </span>
                </span>

                <span className="flagPatternsAction">
                  View {flagCount} flag{flagCount === 1 ? "" : "s"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FlagPatternsPanel;
